import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { CheckSquare2, ExternalLink } from "lucide-react";
import type { QCStandardByIdResult } from "@/modules/qc/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";

interface QCDetailContentProps {
  qc: QCStandardByIdResult;
}

export function QCDetailContent({ qc }: QCDetailContentProps) {
  const nav = useNavigate();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-100 text-green-800">ໃຊ້ງານ</Badge>;
      case "draft":
        return <Badge variant="secondary">ປັບປຸງ</Badge>;
      case "inactive":
        return <Badge variant="destructive">ເລີກໃຊ້</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 print:space-y-4">
      {/* Section 1: ข้อมูลพื้นฐาน */}
      <Card data-tourid="detail-basic-info">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{qc?.name}</span>
            {getStatusBadge(qc?.status ?? "")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ປະເພດງານ
              </h3>
              <p className="text-sm">{qc?.workTypeName || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຂັ້ນຕອນການດໍາເນີນງານ
              </h3>
              <p className="text-sm">{qc?.workStage || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ໃຊ້ກັບພະແນກ
              </h3>
              <p className="text-sm">{qc?.departmentName || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຕໍາແໜ່ງຜູ້ກວດສອບ
              </h3>
              <p className="text-sm">{qc?.inspectorPositionTitle || "-"}</p>
            </div>
            <div className="md:col-span-2">
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຜູ້ກວດສອບ (ກຳນົດຕົວບຸກຄົນ)
              </h3>
              {qc?.inspectors && qc.inspectors.length > 0 ? (
                <ul className="list-inside list-disc text-sm">
                  {qc.inspectors.map((i) => (
                    <li key={i.userId}>
                      {i.name}
                      <span className="text-muted-foreground">
                        {" "}
                        ({i.email})
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm">—</p>
              )}
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ວັນທີອັບເດດລ່າສຸດ
              </h3>
              <p className="text-sm">{formatDateTimeLocal(qc?.updatedAt ?? "")}</p>
            </div>
          </div>

          {qc?.description && (
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ລາຍລະອຽດ
              </h3>
              <p className="text-sm leading-relaxed">{qc?.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: รายการตรวจ */}
      {qc?.items && qc?.items.length > 0 && (
        <Card data-tourid="detail-items">
          <CardHeader>
            <CardTitle className="text-lg">ລາຍການກວດສອບ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {qc?.items.map((item, index) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 rounded-lg border p-4"
                >
                  <div className="mt-0.5 flex-shrink-0">
                    <CheckSquare2 className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-sm">
                        {index + 1}. {item?.title}
                      </h4>
                      {item?.isRequired && (
                        <Badge variant="outline" className="text-xs">
                          ຈໍາເປັນ
                        </Badge>
                      )}
                    </div>
                    {item?.description && (
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {item?.description}
                      </p>
                    )}
                    {item?.passCriteria && (
                      <div className="rounded-md bg-green-50 p-2 dark:bg-green-900">
                        <p className="font-medium text-green-800 text-xs dark:text-green-200">
                          ຜ່ານ: {item?.passCriteria}
                        </p>
                      </div>
                    )}
                    {item?.failCriteria && (
                      <div className="rounded-md bg-red-50 p-2 dark:bg-red-900">
                        <p className="font-medium text-red-800 text-xs dark:text-red-200">
                          ບໍ່ຜ່ານ: {item?.failCriteria}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 3: เกณฑ์ผ่าน / ไม่ผ่าน */}
      {(qc?.passCriteria || qc?.failCriteria) && (
        <Card data-tourid="detail-criteria">
          <CardHeader>
            <CardTitle className="text-lg">ເກນຜ່ານ / ບໍ່ຜ່ານ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {qc?.passCriteria && (
              <div>
                <h3 className="mb-2 font-medium text-sm">ເກນຜ່ານ:</h3>
                <p className="rounded-md bg-green-50 p-3 text-green-800 text-sm leading-relaxed dark:bg-green-900 dark:text-green-200">
                  {qc?.passCriteria}
                </p>
              </div>
            )}
            {qc?.failCriteria && (
              <div>
                <h3 className="mb-2 font-medium text-sm">ເກນບໍ່ຜ່ານ:</h3>
                <p className="rounded-md bg-red-50 p-3 text-red-800 text-sm leading-relaxed dark:bg-red-900 dark:text-red-200">
                  {qc?.failCriteria}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Section 4: แนวทางเมื่อไม่ผ่าน */}
      {qc?.actionOnFail && (
        <Card data-tourid="detail-action-on-fail">
          <CardHeader>
            <CardTitle className="text-lg">ແນວທາງເມື່ອບໍ່ຜ່ານ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm leading-relaxed">{qc.actionOnFail}</p>
            </div>
            {qc.notifyPositionTitle && (
              <div>
                <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                  ສົ່ງແຈ້ງໃຫ້:
                </h3>
                <Badge variant="outline">{qc.notifyPositionTitle}</Badge>
              </div>
            )}
            <div className="flex flex-wrap gap-4 text-sm">
              {qc.requirePhoto && <Badge variant="secondary">ຕ້ອງມີຮູບພາບ</Badge>}
              {qc.requireNotesOnFail && (
                <Badge variant="secondary">ຕ້ອງມີໝາຍເຫດ</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 5: การผูก (Mapping) */}
      {(qc?.checklistId || qc?.kpiId || qc?.checklistName || qc?.kpiName) && (
        <Card data-tourid="detail-mapping">
          <CardHeader>
            <CardTitle className="text-lg">ການຜູກມັດ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(qc.checklistId || qc.checklistName) && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex-1">
                  <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                    Checklist:
                  </h3>
                  {qc.checklistName ? (
                    <p className="font-medium text-sm">{qc.checklistName}</p>
                  ) : (
                    <p className="text-muted-foreground text-sm">-</p>
                  )}
                </div>
                {qc.checklistId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      if (qc.checklistId) {
                        nav({
                          to: "/app/checklists/$id",
                          params: { id: qc.checklistId },
                        });
                      }
                    }}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
            {(qc.kpiId || qc.kpiName) && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex-1">
                  <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                    KPI:
                  </h3>
                  {qc.kpiName ? (
                    <p className="font-medium text-sm">{qc.kpiName}</p>
                  ) : (
                    <p className="text-muted-foreground text-sm">-</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
